import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { WorkflowRequestViewModal } from "@/pages/workflows/widgets/WorkflowRequestViewModal.tsx";
import { useMyApprovalRequests } from "@/queries/approval/use-approval-requests.query.ts";
import {
  ApiApprovalRequestMyFilter,
  ApiApprovalRequestStatus,
  ApiMyWorkflowApprovalRequest,
  getRequestStatusTitle,
} from "@/types/approvals/approval.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  ListItem,
  Stack,
  Tag,
  TagVariants,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const statusVariants: Record<ApiApprovalRequestStatus, TagVariants> = {
  [ApiApprovalRequestStatus.awaiting]: "warning",
  [ApiApprovalRequestStatus.approved]: "positive",
  [ApiApprovalRequestStatus.rejected]: "danger",
  [ApiApprovalRequestStatus.canceled]: "secondary",
};

export const EmployeeWorkflowApprovalRequests = ({ employee }: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { renderDate, renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const [filter, setFilter] = useState<ApiApprovalRequestMyFilter>({ page_number: 1 });
  const { data: requests, isLoading, isFetching } = useMyApprovalRequests(filter);

  const openRequest = useCallback(
    (request: ApiMyWorkflowApprovalRequest) => {
      openModal(WorkflowRequestViewModal, {
        requestId: request.approval_request_details.id,
      });
    },
    [openModal]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiMyWorkflowApprovalRequest>) => (
      <DropdownMenu>
        <DropdownMenuItem onClick={() => openRequest(row.original)} title={t("common:view")} />
      </DropdownMenu>
    ),
    [openRequest, t]
  );

  const columns = useMemo<ColumnDef<ApiMyWorkflowApprovalRequest>[]>(
    () => [
      {
        id: "date",
        header: t("common:date"),
        cell: ({ row }) => (
          <Text>{renderDate(row.original.approval_request_details.datetime_created_at)}</Text>
        ),
        ...ColumnSizes.date,
      },
      {
        id: "title",
        header: t("common:title"),
        cell: ({ row }) => {
          const details = row.original.approval_request_details;
          const workflowData = row.original.workflow_data;
          const isCreatedForAnotherEmployee = details.employee.id !== employee.id;

          return (
            <ListItem
              emoji={workflowData.workflow.emoji ?? undefined}
              titleSlot={
                <DotSeparator>
                  <Text className="truncate text-title">{workflowData.title}</Text>
                  {isCreatedForAnotherEmployee && (
                    <Text className="truncate text-secondary">
                      {renderEmployeeName(details.employee)}
                    </Text>
                  )}
                </DotSeparator>
              }
              forceShowAvatar
              truncate
              behavior="card"
            />
          );
        },
        ...ColumnSizes.wide,
      },
      {
        id: "status",
        header: t("common:status"),
        cell: ({ row }) => {
          const status = row.original.approval_request_details.status;
          return (
            <Stack direction="horizontal">
              <Tag variant={statusVariants[status]}>{getRequestStatusTitle(status)}</Tag>
            </Stack>
          );
        },
        ...ColumnSizes.status,
      },
    ],
    [employee.id, renderDate, renderEmployeeName, t]
  );

  const requestsCount = requests?.metadata.total_items ?? 0;

  return (
    <Stack gap="lg">
      <Stack className="justify-between" direction="horizontal" items="center" gap="md">
        <Title
          header={t("approval:approval_requests")}
          size="lg"
          badge={requestsCount > 0 ? `(${requestsCount})` : undefined}
          badgeVariant="secondary"
        />
      </Stack>

      <HistoryTable<ApiMyWorkflowApprovalRequest>
        historyItems={requests?.items}
        columns={columns}
        canViewHistory
        defaultVisibleRecords={4}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={{
          metadata: requests?.metadata,
          currentPage: filter.page_number ?? 1,
          onPageChange: (page) => {
            setFilter((currentFilter) => ({ ...currentFilter, page_number: page }));
          },
        }}
        emptyStateTitle={t("approval:my_requests_empty_title")}
        stickyLastColumn={false}
        renderRowAction={renderRowAction}
        onRowClick={(row) => openRequest(row.original)}
      />
    </Stack>
  );
};
